import { Client, cacheExchange, fetchExchange, subscriptionExchange } from 'urql';
import { authExchange } from '@urql/exchange-auth';
import { MessageType, createClient as createWsClient } from 'graphql-ws';
import { initGraphQLTada } from 'gql.tada';
import { SUPER_USER_ROLE, claimsAreSuperUser } from '@shared';
import type { introspection } from '../../../graphql-env.d.ts';
import { getClaims, getToken, onTokenChange, refreshToken } from './token';

export const graphql = initGraphQLTada<{
  introspection: introspection;
  scalars: {
    Uuid: string;
    Timestamptz: string;
    Numeric: number;
    Bigint: number;
    Float8: number;
    Jsonb: unknown;
  };
}>();

const httpUrl = process.env.BUN_PUBLIC_HASURA_URL ?? 'http://localhost:8095/v1/graphql';

function authHeaders() {
  const token = getToken();

  const headers: Record<string, string> = {};

  if (!token) return headers;

  headers.authorization = `Bearer ${token}`;

  const claims = getClaims();

  if (claims && claimsAreSuperUser(claims)) headers['x-hasura-role'] = SUPER_USER_ROLE;

  return headers;
}

let restartRequestedBeforeConnected = false;
let gracefullyRestartWs = () => {
  restartRequestedBeforeConnected = true;
};

const wsClient = createWsClient({
  url: httpUrl.replace(/^http/, 'ws'),
  connectionParams: () => {
    const headers = authHeaders();

    return Object.keys(headers).length > 0 ? { headers } : {};
  },
  retryAttempts: Infinity,
  shouldRetry: () => true,
  on: {
    connected: (socket) => {
      gracefullyRestartWs = () => {
        const ws = socket as WebSocket;

        if (ws.readyState === WebSocket.OPEN) ws.close(4205, 'Client Restart');
      };

      if (restartRequestedBeforeConnected) {
        restartRequestedBeforeConnected = false;
        gracefullyRestartWs();
      }
    },
    message: (message) => {
      if (message.type === MessageType.Error && message.payload.some((e) => /jwt/i.test(e.message ?? ''))) {
        gracefullyRestartWs();
      }
    },
  },
});

onTokenChange(() => gracefullyRestartWs());

export const graphqlClient = new Client({
  url: httpUrl,
  preferGetMethod: false,
  exchanges: [
    cacheExchange,
    authExchange(async (utils) => ({
      addAuthToOperation(operation) {
        const headers = authHeaders();

        return Object.keys(headers).length > 0 ? utils.appendHeaders(operation, headers) : operation;
      },
      didAuthError(error) {
        return error.graphQLErrors.some(
          (e) => e.extensions?.code === 'invalid-jwt' || e.extensions?.code === 'access-denied',
        );
      },
      async refreshAuth() {
        await refreshToken();
      },
    })),
    fetchExchange,
    subscriptionExchange({
      forwardSubscription: (operation) => ({
        subscribe: (sink) => ({
          unsubscribe: wsClient.subscribe({ ...operation, query: operation.query ?? '' }, sink),
        }),
      }),
    }),
  ],
});
